import type {
	CommunityCommentListResponse,
	CommunityCommentRequest,
	CommunityIdeaRequest,
	CommunityVoteRequest,
	EmailPreferenceRequest,
	FavoriteRequest,
	FavoritesListResponse,
	FeedbackRequest,
	ProgressRequest,
	TopicVoteRequest,
	NotificationListResponse,
	NotificationSummary,
	ReferralCenterResponse,
	ReferralInviteeListResponse,
} from "./apiContracts";
import { PrototypeContentApiClient } from "./apiContentClient";
import type { PublicIndexResponse, TopicKeywordsResponse } from "./apiTopicContracts";
import { pagedQuery, type PagedListOptions } from "./apiPaging";
import type { AccountDashboardResponse, CategoryInterestsResponse, CommunityIdeaListResponse, LearningStatsResponse, PromotionHistoryResponse, QuotaUsageResponse } from "./apiClientResponses";
import { ResumeCardRequestCoalescer } from "./resumeCardRequest";
import { validateSourceAttachment } from '../../domain/sourceAttachmentPolicy';
import type { PromotionAttribution } from './pendingRedeemIntent';

export { PrototypeApiError } from "./apiTransport";

/** User activity, social, upload, and analytics endpoints layered on content APIs. */
export class PrototypeApiClient extends PrototypeContentApiClient {
	private resumeCardCoalescer = new ResumeCardRequestCoalescer();
	protected override onIdentityScopeChanged(): void {
		super.onIdentityScopeChanged();
		this.resumeCardCoalescer.invalidate();
	}

	listAiCourses(status: "inprogress" | "completed" = "completed", options: PagedListOptions = {}) {
		return this.get(`/api/ai/courses?${pagedQuery({ status, ...options })}`) as Promise<import("./apiContracts").AiCourseListResponse>;
	}

	getTopic(slug: string) { return this.topicDetail(slug); }

	topicKeywords(slug: string) {
		return this.get(`/api/topics/${encodeURIComponent(slug)}/keywords`) as Promise<TopicKeywordsResponse>;
	}

	topicChapters(slug: string) { return this.get(`/api/topics/${encodeURIComponent(slug)}/chapters`); }

	seoCourse(slug: string) { return this.get(`/api/seo/topics/${encodeURIComponent(slug)}`); }

	getAccountDashboard() { return this.get("/api/account/dashboard") as Promise<AccountDashboardResponse>; }

	listListeningHistory(options: PagedListOptions = {}) {
		return this.get(`/api/account/history?${pagedQuery(options)}`) as Promise<{
			items: Array<{ topicId: string; topicSlug: string | null; chapterId: string; progressPercent: number; updatedAt: string; topicTitle: string | null; chapterIndex: number | null; chapterTitle: string | null; coverImageUrl: string | null }>;
			hasMore: boolean;
			nextCursor: string | null;
		}>;
	}

	githubTrending(segment: "daily" | "weekly" | "monthly" | "inprogress" | "completed" = "daily", options: PagedListOptions = {}) {
		return this.get(`/api/github/public-trending?${pagedQuery({ segment, ...options })}`);
	}

	listCommunityIdeas(status: 'recent' | 'completed' = 'recent', options: PagedListOptions = {}, signal?: AbortSignal) {
		return this.get(`/api/community/ideas?${pagedQuery({ status, ...options })}`, signal) as Promise<CommunityIdeaListResponse>;
	}

	submitCommunityIdea(input: CommunityIdeaRequest) { return this.post("/api/community/ideas", input); }

	voteCommunityIdea(ideaId: string, input: CommunityVoteRequest) {
		return this.post(`/api/community/ideas/${encodeURIComponent(ideaId)}/vote`, input);
	}

	commentCommunityIdea(ideaId: string, input: CommunityCommentRequest) {
		return this.post(`/api/community/ideas/${encodeURIComponent(ideaId)}/comments`, input);
	}

	listCommunityIdeaComments(ideaId: string, limit = 200) {
		return this.get(`/api/community/ideas/${encodeURIComponent(ideaId)}/comments?limit=${encodeURIComponent(String(limit))}`) as Promise<CommunityCommentListResponse>;
	}

	submitFeedback(input: FeedbackRequest) { return this.post("/api/feedback", input); }

	updateEmailPreferences(input: EmailPreferenceRequest) { return this.post("/api/email/preferences", input); }

	getEmailPreferences() {
		return this.get("/api/email/preferences") as Promise<{
			preferences: { security: boolean; billing: boolean; product: boolean; marketing: boolean };
			immutable: string[];
			persistence: string;
		}>;
	}

	async updateFavorite(input: FavoriteRequest) {
		const result = await this.post("/api/user/favorites", input);
		this.resumeCardCoalescer.invalidate();
		return result;
	}

	updateTopicVote(input: TopicVoteRequest) { return this.post("/api/user/topic-votes", input); }

	listTopicVotes() { return this.get("/api/user/topic-votes") as Promise<{ votes: { topicId: string; voteValue: number }[] }>; }

	async updateProgress(input: ProgressRequest) {
		const result = await this.post("/api/user/progress", input);
		this.resumeCardCoalescer.invalidate();
		return result;
	}

	recordTopicOpen(topicId: string) { return this.postBackground("/api/user/engagement/topic-open", { topicId }); }

	getResumeCard(limit?: number, signal?: AbortSignal, options: { force?: boolean } = {}) {
		if (options.force) this.resumeCardCoalescer.invalidate();
		return this.resumeCardCoalescer.get(limit, (path) => this.get(path) as Promise<import("./apiContracts").ResumeCardResponse>, signal);
	}

	listFavorites(options: number | PagedListOptions = 20, signal?: AbortSignal) {
		return this.get(`/api/user/favorites?${pagedQuery(typeof options === "number" ? { limit: options } : options)}`, signal) as Promise<FavoritesListResponse>;
	}

	listFavoritesWithTopics(options: number | PagedListOptions = 20, signal?: AbortSignal) {
		const query = pagedQuery({ ...(typeof options === "number" ? { limit: options } : options), details: "1" });
		return this.get(`/api/user/favorites?${query}`, signal) as Promise<FavoritesListResponse & { topics?: PublicIndexResponse["topics"] }>;
	}

	getLearningStats() { return this.get("/api/user/learning-stats") as Promise<LearningStatsResponse>; }

	getQuotaUsage(limit?: number, cursor?: string | null) {
		const params = new URLSearchParams();
		if (limit != null) params.set("limit", String(limit));
		if (cursor) params.set("cursor", cursor);
		const query = params.toString();
		return this.get(`/api/billing/quota-usage${query ? `?${query}` : ""}`) as Promise<QuotaUsageResponse>;
	}

	getPromotionHistory(limit?: number, cursor?: string | null) {
		const params = new URLSearchParams();
		if (limit != null) params.set('limit', String(limit));
		if (cursor) params.set('cursor', cursor);
		const query = params.toString();
		return this.get(`/api/profile/promotions${query ? `?${query}` : ''}`) as Promise<PromotionHistoryResponse>;
	}

	previewPromotion(code: string) { return this.post('/api/promotions/preview', { code }); }
	redeemPromotion(code: string, idempotencyKey: string, attribution?: PromotionAttribution) { return this.postWithHeaders('/api/promotions/redeem', { code, ...(attribution && Object.keys(attribution).length > 0 ? { attribution } : {}) }, { 'idempotency-key': idempotencyKey }); }

	getUnreadNotificationCount() { return this.get('/api/notifications/unread-count') as Promise<{ unreadCount: number }>; }

	getNotificationSummary() { return this.get('/api/notifications/summary') as Promise<NotificationSummary>; }

	listNotifications(options?: { cursor?: string | null; category?: string | null; unread?: boolean; status?: 'all' | 'unread' | 'read'; limit?: number }) {
		const params = new URLSearchParams();
		if (options?.cursor) params.set('cursor', options.cursor);
		if (options?.category) params.set('category', options.category);
		if (options?.unread) params.set('unread', 'true');
		if (options?.status) params.set('status', options.status);
		if (options?.limit) params.set('limit', String(options.limit));
		const query = params.toString();
		return this.get(`/api/notifications${query ? `?${query}` : ''}`) as Promise<NotificationListResponse>;
	}

	markNotificationRead(id: string) {
		return this.post(`/api/notifications/${encodeURIComponent(id)}/read`, {}) as Promise<{ id: string; readAt: string }>;
	}

	markAllNotificationsRead() { return this.post('/api/notifications/read-all', {}) as Promise<{ updated: number; updatedCount: number; unreadCount: number; readAt: string }>; }

	getReferralCenter() { return this.get('/api/referrals/me') as Promise<ReferralCenterResponse>; }

	listReferralInvitees(cursor?: string | null) {
		return this.get(`/api/referrals/invitees?limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`) as Promise<ReferralInviteeListResponse>;
	}

	getCategoryInterests() { return this.get("/api/account/category-interests") as Promise<CategoryInterestsResponse>; }

	verifyTurnstile(token: string, action: string) { return this.post("/api/security/turnstile/verify", { token, action }); }

	presignUpload(input: { purpose: "feedback" | "community" | "ai"; submissionId: string; contentType: string; sizeBytes: number; fileName: string; turnstileToken?: string }) {
		return this.post("/api/uploads/presign", input) as Promise<{ objectKey: string; uploadUrl: string; expiresAt: number; purpose: string }>;
	}

	async uploadAttachment(file: File, purpose: "feedback" | "community" | "ai", submissionId: string, turnstileToken?: string) {
		const validation = validateSourceAttachment(purpose, file);
		if (!validation.ok) {
			throw new Error(purpose === 'feedback' ? 'Unsupported file type. Please use PNG, JPG, or WebP.' : 'Unsupported file type. Supported files: .doc, .docx, .pdf, .txt, .md, .markdown');
		}
		const contentType = validation.contentType;
		const presign = await this.presignUpload({ purpose, submissionId, contentType, sizeBytes: file.size, fileName: file.name, turnstileToken });
		await this.putToUrl(presign.uploadUrl, file, contentType);
		return { name: file.name, objectKey: presign.objectKey, contentType, size: file.size, purpose, submissionId };
	}

	deleteAttachment(objectKey: string) {
		return this.del(`/api/uploads/attachment?key=${encodeURIComponent(objectKey)}`) as Promise<{ ok: boolean }>;
	}

}

export function createPrototypeApiClient() {
	return new PrototypeApiClient();
}
